import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import {
  Checkmark,
  Edit,
  IntentRequestCreate,
  Renew,
  Settings,
  TrashCan,
  View,
  ViewOff,
} from '@carbon/icons-react';
import * as go from 'gojs';
import * as React from 'react';
import {
  Body,
  Button,
  Icon,
  LoadingIcon,
  MenuItem,
  MenuItems,
  Tooltip,
} from 'vet-bones/design-system/components';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { SidebarCard } from 'src/components/sidebarCard/SidebarCard';
import {
  TEST_IDS,
  getProjectModelCardTestId,
  getProjectModelMenuButtonTestId,
} from 'src/constants/testIds';
import { useRemoveProjectModel } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveProjectModel';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useRefreshProjectModel } from 'src/containers/ProjectDiagram/hooks/useRefreshProjectModel';
import {
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { toggleHiddenModelId } from 'src/containers/ProjectDiagram/utils/toggleHiddenModelId';
import { ProjectSidebarModelCheckbox } from 'src/containers/ProjectSidebar/components/ProjectSidebarModelCheckbox';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectSelector } from 'src/selectors/project';
import { projectHiddenModelsSelector } from 'src/selectors/projectHiddenModels';
import { projectIrisForModelIdSelector } from 'src/selectors/projectIrisForModelId';
import { projectModelSelector } from 'src/selectors/projectModel';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { projectModelShouldShowRefreshSelector } from 'src/selectors/projectModelShouldShowRefreshSelector';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { RDFS_SUB_CLASS_OF_IRI } from 'src/templates/queries/iris';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectSidebar/components/projectSidebarModelCard.module.scss';

const useProjectModel = (id: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectModelSelector(projectId, id));
};

const useCanDeleteProjectModel = () => {
  const { projectId } = useRouterParams();
  const modelItems = useReduxSelector(projectModelItemsSelector(projectId));
  return modelItems.length > 1;
};

type ProjectSidebarModelCardProps = {
  idx: number | undefined;
  modelId: string;
};

export const ProjectSidebarModelCard: React.VFC<
  ProjectSidebarModelCardProps
> = ({ idx, modelId }) => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const model = useProjectModel(modelId);
  const canDeleteModel = useCanDeleteProjectModel();
  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );
  const hiddenModels = useReduxSelector(projectHiddenModelsSelector(projectId));
  const isModelListOpen = useReduxSelector(
    (state) => state.ui.modelList.isOpen
  );

  const isHidden = hiddenModels?.includes(modelId) || false;

  const isSelectedModel = targetModelId === modelId;

  const project = useReduxSelector(projectSelector(projectId));
  const isDraft = useReduxSelector(isDraftDiagramSelector(projectId));
  const settingsDisabled = !project || isDraft;
  const shouldShowRefresh = useReduxSelector(
    projectModelShouldShowRefreshSelector(projectId, modelId)
  );
  const { refreshModel, isRefreshingModel } = useRefreshProjectModel(modelId);
  const isRefreshDisabled = isRefreshingModel || isDraft;

  let refreshTooltip;
  if (isDraft) {
    refreshTooltip = copy.components.project.models.refreshDisabledDraft;
  } else if (!isRefreshingModel) {
    refreshTooltip = copy.components.project.models.refresh;
  }

  const irisInModel = useReduxSelector(
    projectIrisForModelIdSelector(projectId, modelId)
  );

  const onClick = React.useCallback(() => {
    if (!diagram || diagram.headless) {
      return;
    }

    const keysToSelect: go.Set<go.Part> = new go.Set();
    diagram.nodes.each((item) => {
      if (item.data.value === RDFS_SUB_CLASS_OF_IRI) {
        const toValue = getModelConceptClassValue(item.data.to);
        const fromValue = getModelConceptClassValue(item.data.from);
        if (irisInModel.includes(toValue) || irisInModel.includes(fromValue)) {
          keysToSelect.add(item);
        }
      } else if (irisInModel.includes(item.data.value)) {
        keysToSelect.add(item);
      }
    });

    commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
      setDiagramSelection(keysToSelect)(diagram)
    );
  }, [diagram, irisInModel]);

  const onClickRefresh = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      refreshModel();
    },
    [refreshModel]
  );

  const onClickTarget = React.useCallback(() => {
    dispatch(
      projectsActionCreators.updatedProjectTargetModelId(projectId, modelId)
    );
  }, [dispatch, modelId, projectId]);

  const onClickRename = React.useCallback(() => {
    dispatch(
      uiSlice.actions.toggledRenameProjectModelDialog({
        isOpen: true,
        modelId,
      })
    );
  }, [dispatch, modelId]);

  const onClickSettings = React.useCallback(() => {
    if (settingsDisabled) {
      return;
    }

    dispatch(
      uiSlice.actions.toggledProjectSettingsDrawer({
        isOpen: true,
      })
    );
  }, [dispatch, settingsDisabled]);

  const onClickDelete = useRemoveProjectModel(modelId);

  const onClickHide = React.useCallback(() => {
    if (!diagram) {
      return;
    }
    commitDiagramModel(diagram, toggleHiddenModelId(modelId));
  }, [diagram, modelId]);

  return (
    <SidebarCard
      actions={
        shouldShowRefresh ? (
          <div className={styles.modelCardActions}>
            <Tooltip
              description={refreshTooltip}
              disabled={!refreshTooltip}
              position={BlueprintPopoverPosition.BOTTOM}
            >
              <Button
                disabled={isRefreshDisabled}
                leftIcon={isRefreshingModel ? LoadingIcon : Renew}
                onClick={onClickRefresh}
                size="2xs"
                testId={TEST_IDS.projectSidebar.modelMenu.refreshButton}
                variant="ghost-gray"
              />
            </Tooltip>
          </div>
        ) : undefined
      }
      className={styles.sidebarCard}
      condensed
      disabled={isHidden}
      menu={
        <MenuItems>
          <Tooltip
            description={copy.components.project.models.hiddenTarget}
            disabled={!isHidden}
            position={BlueprintPopoverPosition.RIGHT}
          >
            <MenuItem
              disabled={isHidden}
              leftIcon={IntentRequestCreate}
              onClick={onClickTarget}
              rightElement={
                isSelectedModel ? (
                  <Icon
                    IconComponent={Checkmark}
                    size="sm"
                    testId={TEST_IDS.projectSidebar.modelMenu.targetCheckmark}
                  />
                ) : undefined
              }
              testId={TEST_IDS.projectSidebar.modelMenu.targetButton}
              text={copy.components.project.models.target}
            />
          </Tooltip>
          <Tooltip
            description={copy.components.project.models.hiddenTarget}
            disabled={!(targetModelId === modelId)}
            position={BlueprintPopoverPosition.RIGHT}
          >
            <MenuItem
              disabled={targetModelId === modelId}
              leftIcon={isHidden ? View : ViewOff}
              onClick={onClickHide}
              testId={TEST_IDS.projectSidebar.modelMenu.hideModelButton}
              text={copy.components.project.models.hide(isHidden)}
            />
          </Tooltip>
          <MenuItem
            leftIcon={Edit}
            onClick={onClickRename}
            testId={TEST_IDS.projectSidebar.modelMenu.renameButton}
            text={copy.components.project.models.rename}
          />
          <Tooltip
            description={copy.components.voicebox.disabledFor.settings}
            disabled={!settingsDisabled}
            position={BlueprintPopoverPosition.RIGHT}
          >
            <MenuItem
              disabled={settingsDisabled}
              leftIcon={Settings}
              onClick={onClickSettings}
              testId={TEST_IDS.projectSidebar.modelMenu.settingsButton}
              text={copy.components.project.models.settings}
            />
          </Tooltip>

          {canDeleteModel ? (
            <MenuItem
              leftIcon={TrashCan}
              onClick={onClickDelete}
              testId={TEST_IDS.projectSidebar.modelMenu.deleteButton}
              text={copy.components.project.models.delete}
            />
          ) : null}
        </MenuItems>
      }
      menuTestId={getProjectModelMenuButtonTestId(modelId, idx)}
      // The card-body click selects the model's nodes on the canvas, which the
      // list view either doesn't have (headless) or covers with the table — so
      // drop the affordance there rather than select invisibly. The menu actions
      // stay. (VET-7007, VET-7253)
      onClick={
        !isHidden && !isModelListOpen && !diagram?.headless
          ? onClick
          : undefined
      }
      selected={isSelectedModel}
      testId={getProjectModelCardTestId(modelId, idx)}
      title={
        <>
          {/* The checkbox only drives canvas selection, which has no analogue in
              a table — and in the headless list view there's no selection to
              reflect, so it would always read unchecked. (VET-7253) */}
          {!isModelListOpen ? (
            <ProjectSidebarModelCheckbox modelId={modelId} />
          ) : null}
          <Body
            ellipsize
            size="md"
            testId={TEST_IDS.projectSidebar.resourceCard.title}
          >
            {model?.name || modelId}
          </Body>
        </>
      }
    />
  );
};
